import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { HandledSaveError } from '@/lib/handled-save-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// =====================================================================
// BusinessInfoPanel — canonical NAP identity editor (WS1 S3).
//
// Loads GET /api/sites/:id/business-info and renders the 8 identity
// fields as controlled inputs. A null canonical value → EMPTY input
// (never prefilled from any legacy/page-content source). Saving PATCHes
// the subset of fields and clears the napNeedsReview flag server-side,
// after which we re-load so the review banner disappears.
//
// `registerSave` (one-primary-CTA-per-step simplification, 2026-08-17):
// the ONLY caller that passes this is the onboarding wizard's foundation
// step — routes/foundation/business-info.tsx (the standalone settings
// page) never does, so it is unaffected and keeps its own always-visible
// Save button exactly as before. When passed, this panel hides its own
// inner Save button/"Saved" indicator and instead exposes `save` as the
// registered function, so the wizard's single bottom "save and continue"
// button performs the NAP save before advancing. `save` re-registers on
// every render (a fresh closure each time, no ref plumbing needed) and
// guards on `loading` so a Continue click that beats this panel's own GET
// is a safe no-op instead of PATCHing the still-empty initial form over
// real saved data. On failure it still sets the same inline error this
// panel has always shown, then RE-THROWS so the wizard's own generic
// "couldn't save this step" handling blocks navigation and the owner can
// fix and retry rather than silently advancing on a failed save.
// =====================================================================

// Every field is nullable on the wire; napNeedsReview is a boolean.
interface BusinessInfo {
  businessName: string | null;
  phone: string | null;
  email: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressRegion: string | null;
  addressPostal: string | null;
  primaryCategory: string | null;
  napNeedsReview: boolean;
}

interface BusinessInfoResponse {
  businessInfo: BusinessInfo;
}

// The 8 editable string fields, in display order.
const FIELDS = [
  'businessName',
  'phone',
  'email',
  'addressStreet',
  'addressCity',
  'addressRegion',
  'addressPostal',
  'primaryCategory',
] as const;

type FieldKey = (typeof FIELDS)[number];

type FormState = Record<FieldKey, string>;

const EMPTY_FORM: FormState = {
  businessName: '',
  phone: '',
  email: '',
  addressStreet: '',
  addressCity: '',
  addressRegion: '',
  addressPostal: '',
  primaryCategory: '',
};

const FIELD_LABELS: Record<FieldKey, string> = {
  businessName: 'Business name',
  phone: 'Phone',
  email: 'Email',
  addressStreet: 'Street address',
  addressCity: 'City',
  addressRegion: 'State / region',
  addressPostal: 'ZIP / postal code',
  primaryCategory: 'Primary category',
};

const FIELD_PLACEHOLDERS: Record<FieldKey, string> = {
  businessName: 'Acme Plumbing',
  phone: '(555) 123-4567',
  email: 'hello@yourbusiness.com',
  addressStreet: '123 Main St',
  addressCity: 'San Diego',
  addressRegion: 'CA',
  addressPostal: '92101',
  primaryCategory: 'Plumber',
};

const FIELD_INPUT_TYPES: Partial<Record<FieldKey, string>> = {
  phone: 'tel',
  email: 'email',
};

// A null canonical value maps to an empty input — never prefilled from
// any other source. This keeps "not set yet" explicit.
function toForm(info: BusinessInfo): FormState {
  return {
    businessName: info.businessName ?? '',
    phone: info.phone ?? '',
    email: info.email ?? '',
    addressStreet: info.addressStreet ?? '',
    addressCity: info.addressCity ?? '',
    addressRegion: info.addressRegion ?? '',
    addressPostal: info.addressPostal ?? '',
    primaryCategory: info.primaryCategory ?? '',
  };
}

export function BusinessInfoPanel({
  siteId,
  registerSave,
}: {
  siteId: string;
  registerSave?: (save: () => Promise<void>) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [needsReview, setNeedsReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const res = await fetch(
      locationApi(businessId, siteId, 'business-info'),
    );
    if (res.status === 401) {
      window.location.href = '/login';
      return;
    }
    if (!res.ok) {
      setError("We couldn't load your business info. Please try again.");
      setLoading(false);
      return;
    }
    const data = (await res.json()) as BusinessInfoResponse;
    setForm(toForm(data.businessInfo));
    setNeedsReview(data.businessInfo.napNeedsReview === true);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function setField(key: FieldKey, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function save(): Promise<void> {
    // Readiness guard (mirrors reputation-step.tsx / pulse-step.tsx's
    // registerSave readiness gate): a wizard Continue click that beats this
    // panel's own initial GET must not PATCH the still-default empty form
    // over real saved data. `loading` starts true and this function
    // re-registers fresh on every render, so once loading flips false the
    // very next registration reads real state.
    if (loading) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // Send all 8 fields; an empty string clears that field server-side.
      const res = await fetch(
        locationApi(businessId, siteId, 'business-info'),
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(form),
        },
      );
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        setError('Something went wrong saving your changes. Please try again.');
        throw new Error('business_info_save_failed');
      }
      setSaved(true);
      // Re-load so the PATCH-cleared review flag (and any normalization)
      // is reflected — this is what makes the warning banner disappear.
      await load();
    } catch (err) {
      // The !res.ok branch above already set the specific copy; anything
      // else (network failure, load() throwing) gets the same honest
      // fallback. Either way, RE-THROW: in the wizard, this is what makes a
      // failed NAP save block Continue's advance instead of silently
      // completing the step (see the file header's registerSave note); the
      // standalone settings-page button below swallows this rejection since
      // `error` is already set for display there.
      if (!(err instanceof Error && err.message === 'business_info_save_failed')) {
        setError('Something went wrong saving your changes. Please try again.');
      }
      // HandledSaveError (dup-banner fix, 2026-08-21): either branch above
      // has set this panel's own inline error copy by now, so the wizard
      // shell must block the advance without adding its generic banner. The
      // standalone settings-page path still just swallows the rejection.
      throw new HandledSaveError(err);
    } finally {
      setBusy(false);
    }
  }

  // Re-registers every render with a fresh closure so `save` always reads
  // the LATEST `form`/`loading` — no ref plumbing needed, since re-running
  // an effect body that only reassigns a ref (inside the wizard's
  // `registerSave`) is harmless. No-op when this panel is used standalone
  // (routes/foundation/business-info.tsx never passes `registerSave`).
  useEffect(() => {
    registerSave?.(save);
  });

  if (loading) {
    return <div className="h-64 animate-pulse rounded-lg bg-muted" />;
  }

  return (
    <div className="space-y-4">
      {needsReview ? (
        <div className="space-y-1.5 rounded-lg border border-warning-bg bg-warning-bg/40 p-4">
          <p className="text-sm font-semibold text-foreground">
            Please review your business details
          </p>
          <p className="text-sm text-text-secondary">
            We found a mismatch between your website and your Google business
            info. Please review the details below and save to confirm.
          </p>
        </div>
      ) : null}

      <div className="space-y-5 rounded-lg border border-border-subtle bg-card p-6">
        <div className="space-y-4">
          {FIELDS.map((key) => (
            <div key={key} className="space-y-1.5">
              <label
                htmlFor={`business-info-${key}`}
                className="block text-sm font-medium text-foreground"
              >
                {FIELD_LABELS[key]}
              </label>
              <Input
                id={`business-info-${key}`}
                type={FIELD_INPUT_TYPES[key] ?? 'text'}
                value={form[key]}
                placeholder={FIELD_PLACEHOLDERS[key]}
                onChange={(e) => setField(key, e.target.value)}
              />
              {key === 'primaryCategory' ? (
                <p className="text-xs text-text-tertiary">
                  Leave blank if you're not sure — we'll help you choose the
                  right Google category later.
                </p>
              ) : null}
            </div>
          ))}
        </div>

        <p className="text-xs text-text-tertiary">
          Empty fields aren't set yet — your site shows the values from your
          page content until you fill them in here.
        </p>

        {error ? (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
            {error}
          </p>
        ) : null}

        {registerSave ? null : (
          // Design audit T8 (A11): right-aligned, and the "Saved" note sits
          // BEFORE the button so the button itself keeps the trailing edge —
          // the same footer geometry as the areas card below it on Business
          // info, which is the only other save on that screen.
          <div className="flex items-center justify-end gap-3">
            {saved && !busy ? (
              <span className="text-sm text-text-secondary">Saved</span>
            ) : null}
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                // `save` now rejects on failure (so the wizard can block
                // navigation on it) — this standalone button already shows
                // the failure via `error` above, so swallow the rejection
                // here rather than let it become an unhandled promise
                // rejection.
                save().catch(() => {});
              }}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
